import { Locale, RedisTtlConfig } from '@headless-workspace/config';
import {
    CachedRepository,
    Result,
    ResultEmpty,
    ResultFailureFromApiError,
    ResultSuccess,
} from '@headless-workspace/core-domain';
import {
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
} from '@headless-workspace/data';
import { CachingHelpers } from '@headless-workspace/utils';
import { NewsletterDTO } from './NewsletterDTO';
import { NewsletterValue, mapNewsletter } from './NewsletterValue';

export interface NewsletterSlotRepositorySpec {
    fetchNewsletterSlot(locale: Locale): Promise<Result<NewsletterValue>>;
}

const SLOT_ID = 'newsletter-content-global';

const CACHE_SERVICE_NEWSLETTER_SLOTS_PREFIX = 'slots';
const CACHE_SERVICE_NEWSLETTER_SLOTS_KEY = 'newsletter';

export class NewsletterSlotRepository extends CachedRepository implements NewsletterSlotRepositorySpec {
    readonly path = '/content/v1';

    constructor() {
        super(memoryCacheAuthStrategy, memoryCacheRefreshTokenStrategy, memoryCacheQueryParamStrategy);
    }

    async fetchRemoteNewsletterSlot(locale: Locale): Promise<Result<NewsletterValue>> {
        try {
            const response = await this.datasource.getResource(`/content/v1/slots/${SLOT_ID}`, {
                locale,
            });

            const newsLetterSlotResponse = mapNewsletter(NewsletterDTO.parse(response));
            if (!newsLetterSlotResponse) {
                return ResultEmpty();
            }

            return ResultSuccess(newsLetterSlotResponse);
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async fetchNewsletterSlot(locale: Locale): Promise<Result<NewsletterValue>> {
        const params = { locale };

        return this.withCache(
            locale,
            CACHE_SERVICE_NEWSLETTER_SLOTS_PREFIX,
            CachingHelpers.createCacheKey(CACHE_SERVICE_NEWSLETTER_SLOTS_KEY, params),
            {
                ...RedisTtlConfig,
                tags: CachingHelpers.createTags(
                    CACHE_SERVICE_NEWSLETTER_SLOTS_PREFIX,
                    CACHE_SERVICE_NEWSLETTER_SLOTS_KEY,
                    params,
                ),
            },
            () => this.fetchRemoteNewsletterSlot(locale),
        );
    }
}
